import styled from 'styled-components';
import { useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Label,
} from 'recharts';

export type NPLTrendRow = {
  label: string; // x축 라벨: ~07.06 / 2025.02 등
  mortgageAmount: number; // 근저당액(원단위)
  claimPrice: number; // 청구금액(원단위)
  winRate: number; // 낙찰률(%)
  winPriceRate: number; // 낙찰가율(%)
  collateralValueRate: number; // 담보가치율(%)
  recoveryRate: number; // 회수율(%)
  lossRate: number; // 손실률(%)
  shortfallRate: number; // 회수부족율(%)
};

type Props = {
  data?: NPLTrendRow[];
  view: 'amount' | 'rates';
};

const H = 340; // 차트 높이
const LEFT_W = 50; // 좌측 축 폭
const RIGHT_W = 40; // 우측 축 폭(잘림 방지)
const TICK_W = 80; // X축 한 칸 폭(스크롤 필요시 조정)

const toNum = (v: any) =>
  typeof v === 'string' ? Number(v.replaceAll(',', '')) : Number(v) || 0;

// MSD 룰 축 최대
const axisMaxByMSDRule = (n: number) => {
  if (!isFinite(n) || n <= 0) return 1;
  const intPart = Math.floor(n);
  if (intPart === 0) return 1;
  const digits = Math.floor(Math.log10(intPart)) + 1;
  const msd = Math.floor(intPart / Math.pow(10, digits - 1));
  return (msd + 1) * Math.pow(10, digits - 1);
};

// 커스텀 툴팁
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;

  const viewMeta = {
    amount: [
      {
        key: 'mortgage_uk',
        label: '근저당액',
        isBar: true,
        img: '/assets/images/icon/gray_box.svg',
      },
      {
        key: 'claim_uk',
        label: '청구금액',
        isBar: true,
        img: '/assets/images/icon/dark_gray_box.svg',
      },
      {
        key: 'winRate',
        label: '낙찰률',
        isBar: false,
        img: '/assets/images/icon/green_dot.svg',
      },
      {
        key: 'winPriceRate',
        label: '낙찰가율',
        isBar: false,
        img: '/assets/images/icon/blue_dot.svg',
      },
    ],
    rates: [
      {
        key: 'collateralValueRate',
        label: '담보가치율',
        isBar: false,
        img: '/assets/images/icon/red_dot.svg',
      },
      {
        key: 'recoveryRate',
        label: '회수율',
        isBar: false,
        img: '/assets/images/icon/purple_dot.svg',
      },
      {
        key: 'lossRate',
        label: '손실률',
        isBar: false,
        img: '/assets/images/icon/orange_dot.svg',
      },
      {
        key: 'shortfallRate',
        label: '회수부족율',
        isBar: false,
        img: '/assets/images/icon/gray_dot.svg',
      },
    ],
  };

  const hasAmountKeys = viewMeta['amount'].some((m) =>
    payload.find((x: any) => x.dataKey === m.key),
  );
  const config = viewMeta[hasAmountKeys ? 'amount' : 'rates'];

  return (
    <TooltipContainer>
      <TooltipHeader>{label}</TooltipHeader>
      <SeriesList>
        {config.map((meta) => {
          const v = payload.find((p: any) => p.dataKey === meta.key);
          if (
            !v ||
            v.value === undefined ||
            v.value === null ||
            Number.isNaN(v.value)
          )
            return null;
          let displayValue;
          if (meta.key === 'mortgage_uk' || meta.key === 'claim_uk') {
            displayValue = <>{Number(v.value).toLocaleString('ko-KR')} 억원</>;
          } else {
            displayValue = <>{Number(v.value).toFixed(1)}%</>;
          }
          return (
            <SeriesItem key={meta.key}>
              <SeriesIcon>
                <img src={meta.img} alt="" width={16} height={16} />
              </SeriesIcon>
              <SeriesLabel>{meta.label}</SeriesLabel>
              <SeriesValue style={{ marginLeft: 'auto' }}>
                {displayValue}
              </SeriesValue>
            </SeriesItem>
          );
        })}
      </SeriesList>
    </TooltipContainer>
  );
}

export default function NPLTrendAnalysisGraph({ data = [], view }: Props) {
  const rateAxisId = view === 'amount' ? 'rate' : 'rateL';
  // 금액 그래프는 억원 단위로 표시
  const chartData = useMemo(() => {
    return data.map((d) => ({
      ...d,
      mortgage_uk: d.mortgageAmount / 1e8, // 억원
      claim_uk: d.claimPrice / 1e8, // 억원
    }));
  }, [data]);

  // 축 최대값
  const amountAxisMax = useMemo(() => {
    const maxV = Math.max(
      0,
      ...chartData.map((d) =>
        Math.max(toNum(d.mortgage_uk), toNum(d.claim_uk)),
      ),
    );
    return axisMaxByMSDRule(maxV);
  }, [chartData]);

  const rateAxisMax = useMemo(() => {
    const values =
      view === 'amount'
        ? [
            ...data.map((d) => d.winRate ?? 0),
            ...data.map((d) => d.winPriceRate ?? 0),
          ]
        : [
            ...data.map((d) => d.collateralValueRate ?? 0),
            ...data.map((d) => d.recoveryRate ?? 0),
            ...data.map((d) => d.lossRate ?? 0),
            ...data.map((d) => d.shortfallRate ?? 0),
          ];
    return axisMaxByMSDRule(Math.max(0, ...values));
  }, [data, view]);

  const innerWidth = Math.max(TICK_W * data.length + 100, 480);

  return (
    <Container>
      <GraphWrap $amount={view === 'amount'}>
        <AxisPane style={{ width: LEFT_W }}>
          <ResponsiveContainer width="100%" height={H}>
            <ComposedChart
              data={chartData}
              margin={{ top: 22, right: 10, left: -10, bottom: 39 }}
              syncId={`npl-${view}`}
            >
              <XAxis dataKey="label" hide />

              {view === 'amount' ? (
                <YAxis
                  yAxisId="amount"
                  tick={{ fill: '#787D87', fontSize: 12 }}
                  axisLine
                  tickLine={{ stroke: '#B9BFC9', strokeWidth: 1 }}
                  domain={[0, amountAxisMax]}
                  tickFormatter={(v) => Number(v).toLocaleString('ko-KR')}
                >
                  <Label
                    value="금액(억)"
                    position="insideTopLeft"
                    angle={0}
                    offset={30}
                    dy={-50}
                    dx={-20}
                    style={{ fill: '#787D87', fontSize: 12, fontWeight: 500 }}
                  />
                </YAxis>
              ) : (
                <YAxis
                  yAxisId="rateL"
                  tick={{ fill: '#787D87', fontSize: 12 }}
                  axisLine
                  tickLine={{ stroke: '#B9BFC9', strokeWidth: 1 }}
                  domain={[0, rateAxisMax]}
                >
                  <Label
                    value="비율"
                    position="insideTopLeft"
                    angle={0}
                    offset={30}
                    dy={-50}
                    style={{ fill: '#787D87', fontSize: 12, fontWeight: 500 }}
                  />
                </YAxis>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </AxisPane>

        <ScrollPane>
          <Inner style={{ width: innerWidth, height: H }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                key={view}
                data={chartData}
                margin={{ top: 12, right: 10, left: 0, bottom: 0 }}
                syncId={`npl-${view}`}
              >
                {/* X축 */}
                <XAxis
                  dataKey="label"
                  tick={{ fill: '#787D87', fontSize: 12 }}
                  axisLine
                  tickLine={{ stroke: '#B9BFC9', strokeWidth: 1 }}
                />

                {view === 'amount' ? (
                  <YAxis yAxisId="amount" domain={[0, amountAxisMax]} hide />
                ) : (
                  <YAxis yAxisId="rateL" domain={[0, rateAxisMax]} hide />
                )}

                {view === 'amount' && (
                  <YAxis yAxisId="rate" domain={[0, rateAxisMax]} hide />
                )}

                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ stroke: '#B9BFC9', strokeDasharray: '3 3' }}
                  wrapperStyle={{ fontSize: 12 }}
                />
                {view === 'amount' && (
                  <Bar
                    yAxisId="amount"
                    dataKey="mortgage_uk"
                    name="근저당액"
                    fill="#E8E9ED"
                    barSize={18}
                    radius={[8, 8, 0, 0]}
                  />
                )}
                {view === 'amount' && (
                  <Bar
                    yAxisId="amount"
                    dataKey="claim_uk"
                    name="청구금액"
                    fill="#424854"
                    barSize={18}
                    radius={[8, 8, 0, 0]}
                  />
                )}
                {view === 'amount' && (
                  <Line
                    yAxisId={rateAxisId}
                    dataKey="winRate"
                    name="낙찰률"
                    stroke="#0F9667"
                    strokeWidth={2.5}
                    type="linear"
                    dot={{ r: 3, strokeWidth: 0, fill: '#0F9667' }}
                    activeDot={{ r: 4 }}
                  />
                )}

                {view === 'amount' && (
                  <Line
                    yAxisId={rateAxisId}
                    dataKey="winPriceRate"
                    name="낙찰가율"
                    stroke="#3F6EF1"
                    strokeWidth={2.5}
                    type="linear"
                    dot={{ r: 3, strokeWidth: 0, fill: '#3F6EF1' }}
                    activeDot={{ r: 4 }}
                  />
                )}

                {view === 'rates' && (
                  <Line
                    yAxisId={rateAxisId}
                    dataKey="collateralValueRate"
                    name="담보가치율"
                    stroke="#F15171"
                    strokeWidth={2.5}
                    type="linear"
                    dot={{ r: 3, strokeWidth: 0, fill: '#F15171' }}
                    activeDot={{ r: 4 }}
                  />
                )}

                {view === 'rates' && (
                  <Line
                    yAxisId={rateAxisId}
                    dataKey="recoveryRate"
                    name="회수율"
                    stroke="#8E51F7"
                    strokeWidth={2.5}
                    type="linear"
                    dot={{ r: 3, strokeWidth: 0, fill: '#8E51F7' }}
                    activeDot={{ r: 4 }}
                  />
                )}
                {view === 'rates' && (
                  <Line
                    yAxisId={rateAxisId}
                    dataKey="lossRate"
                    name="손실률"
                    stroke="#CE5F16"
                    strokeWidth={2.5}
                    type="linear"
                    dot={{ r: 3, strokeWidth: 0, fill: '#CE5F16' }}
                    activeDot={{ r: 4 }}
                  />
                )}
                {view === 'rates' && (
                  <Line
                    yAxisId={rateAxisId}
                    dataKey="shortfallRate"
                    name="회수부족율"
                    stroke="#787D87"
                    strokeWidth={2.5}
                    type="linear"
                    dot={{ r: 3, strokeWidth: 0, fill: '#787D87' }}
                    activeDot={{ r: 4 }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </Inner>
        </ScrollPane>
        {view === 'amount' && (
          <AxisPane style={{ width: RIGHT_W }}>
            <ResponsiveContainer width="100%" height={H}>
              <ComposedChart
                data={chartData}
                margin={{ top: 22, right: -20, left: 10, bottom: 39 }}
                syncId={`npl-${view}`}
              >
                <XAxis dataKey="label" hide />
                <YAxis
                  yAxisId="rate"
                  orientation="right"
                  domain={[0, rateAxisMax]}
                  tick={{ fill: '#787D87', fontSize: 12 }}
                  tickLine={{ stroke: '#B9BFC9' }}
                  axisLine={{ stroke: '#9AA0A6' }}
                >
                  <Label
                    value="비율"
                    position="insideTopRight"
                    offset={30}
                    dy={-50}
                    style={{ fill: '#787D87', fontSize: 12, fontWeight: 500 }}
                  />
                </YAxis>
              </ComposedChart>
            </ResponsiveContainer>
          </AxisPane>
        )}
      </GraphWrap>

      {view === 'amount' ? (
        <LabelRow>
          <DotProgress>
            <LabelMarker $color="#E8E9ED" />
            <LabelTag>근저당액</LabelTag>
          </DotProgress>
          <DotProgress>
            <LabelMarker $color="#424854" />
            <LabelTag>청구금액</LabelTag>
          </DotProgress>
          <DotProgress>
            <ProgressBar $color="#0F9667" />
            <Dot $color="#0F9667" />
            <ProgressBar $color="#0F9667" />
            <LabelTag>낙찰률</LabelTag>
          </DotProgress>
          <DotProgress $width="92px">
            <ProgressBar $color="#3F6EF1" />
            <Dot $color="#3F6EF1" />
            <ProgressBar $color="#3F6EF1" />
            <LabelTag>낙찰가율</LabelTag>
          </DotProgress>
        </LabelRow>
      ) : (
        <LabelRow>
          <DotProgress $width="103px">
            <ProgressBar $color="#F15171" />
            <Dot $color="#F15171" />
            <ProgressBar $color="#F15171" />
            <LabelTag>담보가치율</LabelTag>
          </DotProgress>
          <DotProgress>
            <ProgressBar $color="#8E51F7" />
            <Dot $color="#8E51F7" />
            <ProgressBar $color="#8E51F7" />
            <LabelTag>회수율</LabelTag>
          </DotProgress>
          <DotProgress>
            <ProgressBar $color="#CE5F16" />
            <Dot $color="#CE5F16" />
            <ProgressBar $color="#CE5F16" />
            <LabelTag>손실률</LabelTag>
          </DotProgress>
          <DotProgress $width="103px">
            <ProgressBar $color="#787D87" />
            <Dot $color="#787D87" />
            <ProgressBar $color="#787D87" />
            <LabelTag>회수부족율</LabelTag>
          </DotProgress>
        </LabelRow>
      )}
    </Container>
  );
}

const TooltipContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0px 6px 30px 0px rgba(56, 65, 87, 0.18);
  padding: 16px 18px 12px 18px;
  min-width: 186px;
  max-width: 400px;
`;

const TooltipHeader = styled.div`
  font-weight: 500;
  font-size: 16px;
  leading-trim: NONE;
  line-height: 24px;
  letter-spacing: -2%;
  color: #424854;
  margin-bottom: 16px;
`;

const SeriesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SeriesItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SeriesIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 11px;
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
`;

const SeriesLabel = styled.span`
  font-size: 13px;
  color: #676e7e;
  min-width: 74px;
  margin-right: 12px;
  flex-shrink: 0;
  font-weight: 500;
`;

const SeriesValue = styled.span`
  font-size: 14px;
  color: #111322;
  font-weight: 500;
  white-space: nowrap;
`;

const Container = styled.div.attrs<{ 'data-capture-root'?: string }>(() => ({
  'data-capture-root': '',
}))`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

// ⬇️ 가운데 스크롤 영역에 마크
const ScrollPane = styled.div.attrs<{ 'data-export-expand'?: string }>(() => ({
  'data-export-expand': '',
}))`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

// ⬇️ 내부 실제 너비가 들어가는 엘리먼트에도 마크
const Inner = styled.div.attrs<{ 'data-export-inner'?: string }>(() => ({
  'data-export-inner': '',
}))`
  padding: 0 0 8px 0;
`;

const AxisPane = styled.div``;

const GraphWrap = styled.div<{ $amount?: boolean }>`
  display: grid;
  grid-template-columns: ${LEFT_W}px minmax(0, 1fr) ${RIGHT_W}px;
  align-items: start;
  background: #fff;

  ${({ $amount }) =>
    !$amount && `grid-template-columns: ${LEFT_W}px minmax(0, 1fr);`}
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: flex-start;
  align-self: stretch;
  flex-wrap: wrap;
`;

const DotProgress = styled.div<{ $width?: string }>`
  align-self: center;
  display: flex;
  flex-direction: row;
  gap: 2px;
  width: ${({ $width }) => $width ?? '80px'};
  align-items: center;
  margin: 8px;
`;

const ProgressBar = styled.div<{ $color?: string }>`
  height: 2px;
  flex: 1;
  background-color: ${({ $color }) => $color ?? '#0f9667'};
`;

const LabelMarker = styled.div<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  margin: 4px;
  background-color: ${({ $color }) => $color};
`;

const Dot = styled.div<{ $color?: string }>`
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background-color: ${({ $color }) => $color ?? '#0f9667'};
`;

const LabelTag = styled.div`
  font-size: 13px;
  color: #424854;
  margin-left: 4px;
  font-weight: 500;
`;
